/**
 * 콘텐츠 모델의 계층형 분류 주소 계산기.
 *
 * FAQ·도움말·문서처럼 하나의 모델이 여러 단계 분류 허브를 가지는 경우에 쓴다.
 * 저장소나 프레임워크를 모르며, 공개 API와 ROOT-ADMIN이 같은 계산을 재사용한다.
 */

export interface CategoryTreePresentation {
  readonly basePath: string;
  readonly categoryDepth: number;
}

export interface CategoryTreeTerm {
  readonly id: string;
  readonly slug: string;
  readonly parentId?: string | null;
}

export interface CategoryTreeEntry {
  readonly slug: string;
  readonly terms: ReadonlyArray<{
    readonly id: string;
    readonly taxonomy: string;
  }>;
}

function normalizeBasePath(basePath: string): string {
  return basePath.replace(/\/+$/, "");
}

/**
 * 선택한 말단 분류부터 루트까지의 사슬을 루트→말단 순서로 돌려준다.
 * 끊긴 부모·순환·중복 id·깊이 불일치·말단이 아닌 선택은 null로 닫는다.
 */
export function resolveCategoryTreeChain(
  presentation: CategoryTreePresentation,
  leafId: string,
  terms: readonly CategoryTreeTerm[],
): CategoryTreeTerm[] | null {
  if (!Number.isInteger(presentation.categoryDepth) || presentation.categoryDepth < 1) {
    return null;
  }
  const termsById = new Map(terms.map((term) => [term.id, term]));
  if (termsById.size !== terms.length) return null;
  if (terms.some((term) => term.parentId === leafId)) return null;

  const reversed: CategoryTreeTerm[] = [];
  const visited = new Set<string>();
  let currentId: string | null = leafId;
  while (currentId) {
    if (visited.has(currentId)) return null;
    visited.add(currentId);
    const current = termsById.get(currentId);
    if (!current || !current.slug) return null;
    reversed.push(current);
    currentId = current.parentId ?? null;
  }
  if (reversed.length !== presentation.categoryDepth) return null;
  return reversed.reverse();
}

/** 루트부터 선택 분류까지 모든 허브 주소. */
export function resolveCategoryTreeHubPaths(
  presentation: CategoryTreePresentation,
  termId: string,
  terms: readonly CategoryTreeTerm[],
): string[] | null {
  const chain = resolveCategoryTreeChain(presentation, termId, terms);
  const basePath = normalizeBasePath(presentation.basePath);
  if (!chain || !basePath) return null;
  const paths = [basePath];
  const segments: string[] = [];
  for (const term of chain) {
    segments.push(encodeURIComponent(term.slug));
    paths.push(`${basePath}/${segments.join("/")}`);
  }
  return paths;
}

/** 계층형 분류 모델 항목의 정규 상세 주소. */
export function resolveCategoryTreeEntryPath(
  presentation: CategoryTreePresentation,
  entry: CategoryTreeEntry,
  terms: readonly CategoryTreeTerm[],
): string | null {
  if (!entry.slug) return null;
  const knownIds = new Set(terms.map((term) => term.id));
  const categoryIds = [
    ...new Set(
      entry.terms
        .filter((term) => term.taxonomy === "category" && knownIds.has(term.id))
        .map((term) => term.id),
    ),
  ];
  if (categoryIds.length !== 1) return null;
  const hubs = resolveCategoryTreeHubPaths(
    presentation,
    categoryIds[0]!,
    terms,
  );
  const leafPath = hubs?.at(-1);
  return leafPath ? `${leafPath}/${encodeURIComponent(entry.slug)}` : null;
}
